import { OverWaterProtectionSettings } from "verdi-common";
import type { VertexInfo } from "verdi-common/schemas/commonSubSchemas/vertexInfo.js";
import { Organization as OrganizationSchema } from "verdi-common/schemas/organization/organization";
import { SensorReadingConfiguration } from "verdi-common/schemas/sensorReadingConfiguration/sensorReadingConfiguration";
import { ImageOverlay } from "verdi-common/schemas/organization/subSchemas/imageOverlay.js";
import { UserSMSSettings } from "verdi-common/schemas/organization/subSchemas/userSMSSettings.js";
import * as APICommands from "../APICommands/index.js";
import { ModelBase } from "./ModelBase.js";

export class User extends ModelBase {
    SMSSettings?: UserSMSSettings;
    defaultOverWaterProtectionSettings?: Partial<OverWaterProtectionSettings>;
    sensorReadingConfiguration?: SensorReadingConfiguration;
    imageOverlays: ImageOverlay[] = [];

    defaultCoordinates?: VertexInfo;

    /**
     * @deprecated
     */
    devicePrefix;
    /**
     *
     */
    webPreferences;

    /**
     * @type: {{email: string,  phone: string, other: string: other2: string}}
     */
    contactInfo;

    /**
     * @type {boolean}
     */
    deleted: boolean = false;

    /**
     * @type {boolean}
     */
    adminUseOnly: boolean = false;

    constructor(json) {
        super(json);
        this.dbID = json._id || json.id;
        this.category = "User";
        this.assignFromJSON(json);
        this.load();
    }

    /**
     * Creates a new entity of this type and sends it to the server. When the server accepts the new entity, a new local entity
     * is created and this function returns.
     * @constructor
     * @param {string} name
     * @param {Number[]}  defaultPosition - [lat, lng] coordinate pair
     * @param {UserInfo} options
     * @return {Promise<User>}
     */
    static async createNew(name, defaultPosition, options) {
        options.name = name;
        if (Array.isArray(defaultPosition) && defaultPosition.length >= 2) {
            options.defaultCoordinates = { lat: defaultPosition[0], lng: defaultPosition[1] };
        }
        const result = await APICommands.UserAPI.addOne(options);
        // @ts-ignore
        if (result.user) {
            // @ts-ignore
            return new User(result.user);
        }
        return new User(result);
    }

    /**
     * Deletes this entity and returns when the entity has been deleted from the server.
     */
    async delete() {
        //await APICommands.UserAPI.deleteOne(this.id);
        await super.delete();
    }

    /**
     *
     * @return {Promise<void>}
     * @param {UserInfo} newUserInfo
     */
    async edit(newUserInfo: Partial<OrganizationSchema>): Promise<User> {
        await APICommands.UserAPI.updateOne(this.dbID, newUserInfo).then((json) => {
            this.assignFromJSON(json);
        });

        super.edit();

        return this;
    }

    assignFromJSON(json: OrganizationSchema) {
        super.assignFromJSON(json);
        // currently uses ImageOverlays, should eventually be refactored ot use ImageOverlays
        // no other code has been written for this migration
        this.imageOverlays = json.imageOverlays || json.ImageOverlays;
        this.sensorReadingConfiguration = json.sensorReadingConfiguration;
        this.defaultCoordinates = json.defaultCoordinates;
        this.SMSSettings = json.SMSSettings;
        this.name = json.name;
        this.webPreferences = json.webPreferences;
        this.defaultOverWaterProtectionSettings = json.defaultOverWaterProtectionSettings;
        this.contactInfo = json.contactInfo;
        this.pumps = json.pumps;
        this.deleted = json.deleted;
        this.adminUseOnly = json.adminUseOnly;
    }

    /**
     * Gets a simple object to represent this user.
     * @return {UserInfo}
     */
    getJSON(): OrganizationSchema {
        const json: Partial<OrganizationSchema> = {};
        json.name = this.name;
        json.sensorReadingConfiguration = this.sensorReadingConfiguration;
        json.defaultCoordinates = this.defaultCoordinates;
        json.webPreferences = this.webPreferences;
        json.contactInfo = this.contactInfo;
        json.SMSSettings = this.SMSSettings;
        json.pumps = this.pumps;
        json.deleted = this.deleted;
        json.adminUseOnly = this.adminUseOnly;
        return json as OrganizationSchema;
    }

    pumps = [];
}
